import { fetchWidgetData } from '@api/dataSource';
import { getTableYAxisIndex } from './combineSeriesData';

/**
 * Fetch custom query data for a line chart and convert it to series format.
 * Shared between DashboardEditor and WidgetWizard preview flows to avoid duplication.
 *
 * @param {Object} params
 * @param {Object} params.customQuerySource - The custom_query data source object
 * @param {Object} params.widgetConfig - The full widget configuration
 * @param {Function} params.convertFn - (apiData, type, config) => widgetData
 * @param {boolean} params.hasMetricValues - Whether a metric_values source is present
 * @param {number} params.tableSourceCount - Number of table data sources (for Y-axis indexing)
 * @param {number} params.previewRowLimit - Row limit for preview queries
 * @returns {Promise<Array>} Array of series data objects
 */
export async function fetchCustomQueryLineData({
  customQuerySource,
  widgetConfig,
  convertFn,
  hasMetricValues,
  tableSourceCount,
  previewRowLimit,
}) {
  if (!customQuerySource?.config?.query?.trim()) {
    return [];
  }

  try {
    const response = await fetchWidgetData(
      'line',
      widgetConfig,
      {},
      true,
      previewRowLimit,
    );

    if (response.status !== 'success' || !response.data?.rows?.length) {
      return [];
    }

    const customData = convertFn(response.data, 'line', widgetConfig);
    if (!customData) {
      return [];
    }

    const seriesData = [];
    const yAxisIndex = getTableYAxisIndex(hasMetricValues, tableSourceCount);

    if (customData.series) {
      customData.series.forEach((s, idx) => {
        seriesData.push({
          ...s,
          name: s.name?.startsWith('Custom Query') ? s.name : `Custom Query: ${s.name}`,
          categories: idx === 0 ? customData.categories : undefined,
          yAxisIndex,
        });
      });
    } else if (customData.categories && customData.values) {
      seriesData.push({
        name: 'Custom Query',
        data: customData.values,
        categories: customData.categories,
        yAxisIndex,
      });
    }

    return seriesData;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to fetch custom query line data:', err);
    return [];
  }
}
